import React, { useEffect, useState } from 'react'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Field,
  Label,
} from '@headlessui/react'
import { cx } from 'class-variance-authority'
import { CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/24/solid'
import { Office } from '../firms/firm/mapper'
import { useApplyFormStore } from '../../stores/ui/applyFormStore'
import { ErrorMessage } from '../ui/form/ErrorMessage'

interface OfficeSelectorProps {
  offices: NonNullable<Office>[]
  error?: string
  name?: string
}

export const OfficeSelector = ({
  offices,
  error,
  name,
}: OfficeSelectorProps) => {
  const isDirty = useApplyFormStore((state) => state.isDirty)
  const selectedOffice = useApplyFormStore((state) => state.office)
  const setSelectedOffice = useApplyFormStore((state) => state.setOffice)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!selectedOffice && offices.length === 1 && offices[0]?.uuid) {
      setSelectedOffice(offices[0]?.uuid)
    }
  }, [])

  const filteredOffices =
    query === ''
      ? offices
      : offices.filter((office) => {
          if (!office.name && !office.city) return false
          const officeName = office.name?.toLowerCase() || ''
          const queryName = query.toLowerCase()
          return (
            officeName.includes(queryName) ||
            office.city?.toLowerCase()?.includes(queryName)
          )
        })

  function getSelectedOffice(officeUuid: string) {
    return offices.find((office) => office.uuid === officeUuid)
  }

  return (
    <Field className="space-y-2">
      <Label className="font-medium text-night text-sm/6">
        Quel bureau vous intéresse?
      </Label>
      <Combobox
        value={selectedOffice}
        onChange={setSelectedOffice}
        onClose={() => setQuery('')}
      >
        <div className="relative">
          <ComboboxInput
            data-testid="office-selector-input"
            name={name}
            className={cx(
              'flex flex-row items-center w-full gap-2 p-3 px-3 border-2 border-gray-200 rounded-xl h-9 disabled:cursor-not-allowed placeholder:text-sm text-sm focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 relative placeholder:text-night text-night',
              Boolean(error) && 'border-red-300',
            )}
            displayValue={(officeUuid: string) => {
              const office = getSelectedOffice(officeUuid)
              return office?.name || ''
            }}
            placeholder="Rechercher un bureau par ville"
            autoComplete="off"
            required
            onChange={(event) => setQuery(event.target.value)}
          />
          <ComboboxButton className="group absolute inset-y-0 right-0 px-2.5">
            <ChevronDownIcon className="w-5 h-5 fill-night/60 group-data-[hover]:fill-night" />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          data-testid="office-selector-options"
          anchor="bottom"
          transition
          className="origin-top border transition duration-200 ease-out empty:invisible data-[closed]:scale-95 data-[closed]:opacity-0 w-[700px] bg-white rounded-md z-50"
          style={{ zIndex: 9999 }}
        >
          {filteredOffices.map((office) => (
            <ComboboxOption
              key={office.uuid}
              data-testid={`office-selector-option-${office.uuid}`}
              value={office.uuid}
              className="data-[focus]:bg-very-light-blue px-2 py-1 cursor-pointer inline-flex items-center justify-between w-full"
            >
              <span className="text-sm text-night">{office.name}</span>
              {office.uuid === selectedOffice && (
                <CheckCircleIcon className="w-5 h-5" />
              )}
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </Combobox>
      {isDirty && <ErrorMessage error={error} />}
    </Field>
  )
}
